import React, { useState } from 'react';
import { Button, CircleButton, EasyModal, PageHeader } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import {
  useChangeCommandStatusMutation,
  useGetCommandsBulkQuery,
  useLazyGetCommandOperatorsListQuery,
  useLazyGetSingleCommandQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-commands.api';
import { useGetUsersListWithRolesQuery } from '@app/store/api/qm-module/qm-users/qm-users.api';
import RubberTable from '@app/components/rubber-table';
import { TableColumn, TableItem } from '@app/components/rubber-table/table.types';
import {
  QmCommandsListType,
  QmCommandsOperatorsList,
  QmCommandsStatutesTypes,
} from '@app/interfaces/qm-module/qm-manage/qm-commands.type';
import AnalyticsStatus, {
  StatusVariant,
} from '@app/components/ui/analytics-status/analytics-status';

import { toast } from 'react-toastify';

import CommandOperatorsForm from './blocks/command-operators-form';
import QmCommandForm from './blocks/command-form';

const statusChangeMap: Record<
  Exclude<QmCommandsStatutesTypes, 'disbanded'>,
  Exclude<QmCommandsStatutesTypes, 'new'>
> = {
  active: 'disbanded',
  new: 'active',
};

const QmManageCommands = () => {
  const { t } = useTranslation('pages.qmManage.commands');
  const [formModalState, setFormModalState] = useState<boolean>(false);
  const [commandFormData, setCommandFormData] = useState<QmCommandsListType>();
  const [filter, setFilter] = useState<string>();
  const [commandOperatorsModalData, setCommandOperatorsModalData] =
    useState<QmCommandsOperatorsList>();
  // api
  const [changeStatus] = useChangeCommandStatusMutation();
  const [getSingleOperatorsList, { data }] = useLazyGetCommandOperatorsListQuery();
  const [getSingleCommandInfo] = useLazyGetSingleCommandQuery();
  const { data: commandsList } = useGetCommandsBulkQuery();
  const { data: usersWithRolesListOperators } = useGetUsersListWithRolesQuery({
    limit: 100,
    offset: 0,
    sortDesc: true,
    role_qm: 'operator',
    filter: filter ? (encodeURI(filter).split(' ') as unknown as string) : undefined,
  });

  const { data: usersWithRolesListSupervisors } = useGetUsersListWithRolesQuery({
    limit: 100,
    offset: 0,
    sortDesc: true,
    role_qm: 'supervisor',
    filter: filter ? (encodeURI(filter).split(' ') as unknown as string) : undefined,
  });

  // api

  function handleChangeCommandStatus(prevStatus: QmCommandsStatutesTypes, team_id: string) {
    const status = statusChangeMap[prevStatus];
    changeStatus({ body: { status }, params: { team_id } });
  }

  function handleOpenEditSettings(id: string) {
    getSingleCommandInfo({ team_id: id })
      .unwrap()
      .then((data) => {
        setCommandFormData(data);
        setFormModalState(true);
      });
  }

  function handleOpenOperatorsModal(team_id: string) {
    getSingleOperatorsList({ team_id })
      .unwrap()
      .then((data) => {
        setCommandOperatorsModalData(data);
      });
  }

  function onCloseModal() {
    setFormModalState(false);
    setCommandFormData(undefined);
  }

  function closeOperatorsModal() {
    setCommandOperatorsModalData(undefined);
  }

  const tableColumns: TableColumn[] = [
    { index: 'name', title: t('table.name') },
    { index: 'dt_add', title: t('table.dt_add') },
    { index: 'supervisor', title: t('table.supervisor') },
    { index: 'count_operators', title: t('table.count_operators') },
    { index: 'status', title: t('table.status') },
    // { index: 'operators', title: t('table.operators') },
    { index: 'actions', title: t('table.actions'), maxWidth: 110, divider: 'left' },
  ];
  const tableData: TableItem[] =
    commandsList?.qm_teams?.map((item) => ({
      ...item,
      supervisor: item?.supervisor?.name,
      operators: '',

      status: (
        <div className="w-fit flex items-center gap-[10px]">
          <AnalyticsStatus
            additionalStatusList={{
              NEW: { title: t('statuses.NEW') },
              ACTIVE: { title: t('statuses.ACTIVE') },
              DISBANDED: { title: t('statuses.DISBANDED') },
            }}
            status={item.status.toUpperCase() as StatusVariant}
          />
          {item.status !== 'disbanded' && (
            <Button
              label={
                item.status === 'active' ? t('disband') : item.status === 'new' ? t('start') : ''
              }
              fill="linked"
              onClick={() => {
                if (!item.supervisor) {
                  toast.error(t('change_status_err'));
                  return;
                }
                handleChangeCommandStatus(item.status, item.team_id);
              }}
            />
          )}
        </div>
      ),
      actions: (
        <div className="flex  items-center gap-[5px] justify-start w-full">
          <CircleButton
            onClick={() => handleOpenEditSettings(item.team_id)}
            icon={'SettingIcon'}
            size={19}
            hintTitle={t('settings_hint_title')}
            disable={item.status === 'disbanded'}
          />
          <CircleButton
            onClick={() => handleOpenOperatorsModal(item.team_id)}
            icon={'UserIcon'}
            hintTitle={t('operators_hint_title')}
            // disable={!!group.members}
            // className="text-basic_red"
            size={19}
            // disable={item.status === 'disbanded'}
          />
        </div>
      ),
    })) || [];

  return (
    <>
      <PageHeader label={t('pageTitle')} icon="Person3Icon">
        <Button
          onClick={() => setFormModalState(true)}
          icon="CirclePlusIcon"
          label={t('addFormButtonLabel')}
        />
      </PageHeader>

      <div>
        <RubberTable dataSource={tableData} columns={tableColumns} />
      </div>

      <EasyModal
        label={t('create_command_form.label')}
        show={formModalState}
        onClose={onCloseModal}
        withoutFooter
        variant="mediumW650"
      >
        {formModalState && (
          <QmCommandForm
            commandFormData={commandFormData}
            onClose={onCloseModal}
            usersWithRolesList={usersWithRolesListSupervisors?.qm_users || []}
          />
        )}
      </EasyModal>
      <EasyModal
        label={t('operators_modal.label')}
        variant="largeW1127"
        withoutFooter
        show={!!commandOperatorsModalData}
        onClose={closeOperatorsModal}
      >
        {commandOperatorsModalData && (
          <CommandOperatorsForm
            operators={usersWithRolesListOperators?.qm_users || []}
            currentOperators={data}
            filter={filter}
            setFilter={setFilter}
          />
        )}
      </EasyModal>
    </>
  );
};

export default QmManageCommands;
